import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  BodyMediumBold,
  BodySmall,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  Card,
  CardFooter,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {selectApplicantInformation} from 'src/selectors/crm';
import {useSelector} from 'react-redux';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import css from './resume-fields.css';
import {Checkbox} from '@spaced-out/ui-design-system/lib/components/Checkbox';
import {AllExpriences} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-expreriences/all-experiences';
import {AllEducations} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-educations/all-educations';
import type {ExperienceType} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-expreriences/edit-experience-card';
import type {educationType} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-educations/edit-education-card';
// Define types

type FormData = {
  name: string;
  email: string;
  phone: string;
  yearsExp: string;
  monthsExp: string;
  location: string;
  experiences: Array<ExperienceType>;
  educations: Array<educationType>;
};

type Props = {
  setResumeData: (prevResumeData: FormData) => void;
  setIsDisable: (isDisabled: boolean) => void;
};

export const ResumeFields = ({setResumeData, setIsDisable}: Props) => {
  const {t} = useLabelTranslation();
  const applicantDetails = useSelector(selectApplicantInformation);

  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    email: '',
    phone: '',
    yearsExp: '',
    monthsExp: '',
    location: '',
    experiences: [],
    educations: [],
  });

  const formFields = [
    {
      name: 'name',
      label: t('Name', 'Name'),
      placeholder: t('Enter_name', 'Enter name'),
    },
    {
      name: 'email',
      label: t('Email', 'Email'),
      placeholder: t('Enter_email', 'Enter email'),
    },
    {
      name: 'phone',
      label: t('Phone_Number', 'Phone Number'),
      placeholder: t('Enter_phone', 'Enter phone'),
    },
    {
      name: 'location',
      label: t('Current_Location', 'Current Location'),
      placeholder: t('Enter_location', 'Enter location'),
    },
  ];

  const isFormValid = () => {
    const {name, email, phone, yearsExp, monthsExp, location} = formData;
    return (
      name.trim() &&
      email.trim() &&
      phone.trim() &&
      location.trim() &&
      yearsExp !== null &&
      monthsExp !== null
    );
  };

  React.useEffect(() => {
    setIsDisable(!isFormValid());
  }, [formData, setIsDisable]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    const totalExp =
      (parseInt(formData.yearsExp, 10) || 0) +
      (parseInt(formData.monthsExp, 10) || 0) / 12;

    const resume = {
      ...applicantDetails.resume,
      name: formData.name,
      emails: [formData.email],
      phones: [formData.phone],
      currentLocation: formData.location,
      yearsOfExperience: totalExp,
      filePath: applicantDetails.resumeFilekey,
    } as const;

    // setResumeData((prevResumeData) => ({
    //   ...prevResumeData,
    //   ...resume,
    // }));
    // @ts-expect-error - TS2345 - Argument of type '{ readonly name: string; readonly emails: readonly [string]; readonly phones: readonly [string]; readonly currentLocation: string; readonly yearsOfExperience: number; readonly filePath: string; readonly id?: string; ... 25 more ...; readonly new?: boolean; }' is not assignable to parameter of type 'FormData'.
    setResumeData(resume);
  };

  return (
    <div className={css.resumeFieldsContainer}>
      <BodyMediumBold>
        {t(
          'Step_1_Add_Candidate_Information',
          'Step 1: Add Candidate Information',
        )}
      </BodyMediumBold>
      {formFields.map((field) => (
        <Input
          key={field.name}
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          required
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'FormData'.
          value={formData[field.name]}
          onChange={handleInputChange}
        />
      ))}

      <div className={css.twoInputContainer}>
        <Input
          name="yearsExp"
          type="number"
          placeholder={t('years', 'years')}
          label={t('Total_Experience', 'Total Experience')}
          required
          value={formData.yearsExp}
          onChange={handleInputChange}
        />
        <Input
          name="monthsExp"
          type="number"
          placeholder={t('months', 'months')}
          required
          value={formData.monthsExp}
          onChange={handleInputChange}
          classNames={{wrapper: css.maxExpInput}}
        />
      </div>
      <div className={css.emptyStateContainer}>
        <AllExpriences setFormData={setFormData} />
      </div>

      <div className={css.emptyStateContainer}>
        <AllEducations setFormData={setFormData} />
      </div>
    </div>
  );
};
